import React from 'react';
import { SectionHeader } from './SectionHeader';
import { CardContainer } from './CardContainer';
import { StatusPill } from '../design-system/StatusPill';
import { Typography } from '../design-system/typography';
import { colors, spacing } from '../design-system/tokens';

// Tracking icon SVG (matching Figma design)
const TrackingIcon: React.FC = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M21 10C21 17 12 23 12 23S3 17 3 10C3 5.58172 6.58172 2 11 2H13C17.4183 2 21 5.58172 21 10Z" 
      stroke="currentColor" 
      strokeWidth="2"
    />
    <circle 
      cx="12" 
      cy="10" 
      r="3" 
      stroke="currentColor" 
      strokeWidth="2"
    />
  </svg>
);

export interface TrackingEvent {
  id: string;
  date: string;
  time: string;
  title: string;
  location: string;
  status: 'completed' | 'estimated';
  isEstimated?: boolean;
}

export interface TrackingData {
  status: 'in-transit' | 'delivered' | 'delayed';
  events: TrackingEvent[];
}

interface TrackingCardProps {
  data: TrackingData;
  className?: string;
}

const TrackingCard: React.FC<TrackingCardProps> = ({ data, className = '' }) => {
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'in-transit': return 'positive';
      case 'delivered': return 'neutral';
      case 'delayed': return 'negative';
      default: return 'neutral';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'in-transit': return 'In Transit';
      case 'delivered': return 'Delivered';
      case 'delayed': return 'Delayed';
      default: return status;
    }
  };

  const groupEventsByLocation = (events: TrackingEvent[]) => {
    const grouped: Record<string, TrackingEvent[]> = {};
    events.forEach(event => {
      const location = event.location.split(' ')[1] || event.location; // Extract location name
      if (!grouped[location]) {
        grouped[location] = [];
      }
      grouped[location].push(event);
    });
    return grouped;
  };

  const groupedEvents = groupEventsByLocation(data.events);

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      <CardContainer variant="secondary">
        {/* Tracking Header with Status */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.lg }}>
          <SectionHeader 
            title="Shipment Tracking"
            icon={<div style={{ width: '24px', height: '24px' }}><TrackingIcon /></div>}
            showBottomBorder={false}
          />
          <StatusPill variant={getStatusVariant(data.status)}>
            {getStatusText(data.status)}
          </StatusPill>
        </div>

        {/* Divider */}
        <div style={{ 
          height: '1px',
          backgroundColor: colors.status.neutral,
          marginBottom: spacing.lg
        }} />

        {/* Timeline Events by Location */}
        {Object.entries(groupedEvents).map(([location, events], locationIndex) => {
          const allEvents = data.events; // Get all events in order
          
          return (
            <div key={location} style={{ marginBottom: spacing.xl }}>
              {/* Location Header */}
              <Typography 
                variant="h3" 
                as="h3"
                style={{ 
                  color: colors.text.primary,
                  marginBottom: spacing.lg,
                  textTransform: 'uppercase',
                  fontWeight: 'bold'
                }}
              >
                {location}
              </Typography>

              {/* Events for this location */}
              {events.map((event, eventIndex) => {
                // Find this event's position in the global array
                const globalEventIndex = allEvents.findIndex(e => e.id === event.id);
                const nextEvent = allEvents[globalEventIndex + 1];
                const isLastEvent = globalEventIndex === allEvents.length - 1;
                
                // Only show lines between completed events
                const showLine = !isLastEvent && nextEvent && 
                                event.status === 'completed' && 
                                nextEvent.status === 'completed';
                
                let lineColor = '#169959'; // Always green when showing line
                
                // Calculate line height - need to reach next event
                let lineHeight = '66px'; // Default
                if (!isLastEvent && nextEvent) {
                  // Check if next event is in different location
                  const nextEventLocation = nextEvent.location.split(' ')[1] || nextEvent.location;
                  const currentLocation = event.location.split(' ')[1] || event.location;
                  
                  if (nextEventLocation !== currentLocation) {
                    // Next event is in different location, need longer line
                    lineHeight = '110px';
                  }
                }

                return (
                  <div key={event.id} style={{ 
                    position: 'relative',
                    height: '51px',
                    width: '100%',
                    marginBottom: spacing.lg
                  }}>
                    {/* Date (top left) */}
                    <div style={{
                      position: 'absolute',
                      left: '0px',
                      top: '2px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#4E5657',
                      textTransform: 'uppercase',
                      fontFamily: 'Reddit Sans',
                      letterSpacing: '0.28px'
                    }}>
                      {event.date}
                    </div>

                    {/* Time (bottom left) */}
                    <div style={{
                      position: 'absolute',
                      left: '0px',
                      top: '21px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#4E5657',
                      textTransform: 'uppercase',
                      fontFamily: 'Reddit Sans',
                      letterSpacing: '0.28px'
                    }}>
                      {event.isEstimated ? '(ESTIMATED)' : event.time}
                    </div>

                    {/* Timeline dot */}
                    <div style={{
                      position: 'absolute',
                      left: '117px',
                      top: '5px',
                      width: '13px',
                      height: '13px',
                      borderRadius: '50%',
                      backgroundColor: event.status === 'completed' ? '#169959' : '#D7DADB'
                    }} />

                    {/* Connecting line to next event - only between completed events */}
                    {showLine && (
                      <div
                        style={{
                          position: 'absolute',
                          left: '123px', // Center of dot: 117px + 6.5px ≈ 123px
                          top: '18px', // Bottom of dot: 5px + 13px = 18px
                          width: '2px',
                          height: lineHeight,
                          backgroundColor: lineColor
                        }}
                      />
                    )}

                    {/* Event title */}
                    <div style={{
                      position: 'absolute',
                      left: '161px',
                      top: '0px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#000000',
                      fontFamily: 'Reddit Sans',
                      letterSpacing: '0.32px'
                    }}>
                      {event.title}
                    </div>

                    {/* Event location */}
                    <div style={{
                      position: 'absolute',
                      left: '161px',
                      top: '21px',
                      fontSize: '14px',
                      fontWeight: 'normal',
                      color: '#4E5657',
                      fontFamily: 'Reddit Sans',
                      letterSpacing: '0.28px',
                      textTransform: 'uppercase'
                    }}>
                      {event.location}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </CardContainer>
    </div>
  );
};

export default TrackingCard;